import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Card, CardBody, CardHeader,
    Row, Col, Badge, Button, Spinner, Alert, ListGroup, ListGroupItem, Input, Label
} from "reactstrap";
import DatePicker from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { getCEbyUserId, updateClientEligibilityByMemberId } from "../../slices/admin/thunk";

// ^^^ Ajustá estos imports a tus rutas/nombres reales.
//    - updateClientEligibilityByMemberId ya lo usabas para fechas
//    - updateCEByMemberId: usá el nombre del thunk que tengas para actualizar perfil (first/last/email/phone)

function fmtDate(d) {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleString();
}

function formatMDY(d) {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
}

function yesNo(v) {
    return v ? "Yes" : "No";
}

function smallMuted(label, value) {
    return (
        <div className="mb-2">
            <div className="text-muted">{label}</div>
            <div className="fw-semibold">{value ?? "—"}</div>
        </div>
    );
}

function renderAllergy(a) {
    const id = a?.allergyId ?? a?.patientAllergyId ?? "—";
    const active = a?.active === true;
    const label = a?.allergy?.name || `Allergy ID ${id}`;
    return (
        <div className="d-flex align-items-center justify-content-between w-100">
            <span>{label}</span>
            <Badge color={active ? "success" : "secondary"}>{active ? "Active" : "Inactive"}</Badge>
        </div>
    );
}

function renderCondition(c) {
    const name = c?.name || c?.conditionName || c?.medicalCondition?.condition;
    const code = c?.code || c?.icd || c?.icd10;
    const id = c?.id || c?.medicalConditionId;
    const label = name || code || (id ? `Condition ${id}` : "Condition");
    const active = c?.active === true;
    return (
        <div className="d-flex align-items-center justify-content-between w-100">
            <span>{label}</span>
            <Badge color={active ? "success" : "secondary"}>{active ? "Active" : "Inactive"}</Badge>
        </div>
    );
}

function prettyDob(mmddyyyy) {
    if (!mmddyyyy || typeof mmddyyyy !== "string" || mmddyyyy.length !== 8) return "—";
    const mm = mmddyyyy.slice(0, 2);
    const dd = mmddyyyy.slice(2, 4);
    const yyyy = mmddyyyy.slice(4);
    return `${mm}-${dd}-${yyyy}`;
}

function renderDependent(d) {
    const name = [d?.firstName, d?.mi || d?.middleInitial, d?.lastName].filter(Boolean).join(" ");
    const dob = d?.dob ? prettyDob(d?.dob) : null;
    const gender = d?.gender || d?.sex || "—";
    const type = d?.type;
    return (
        <div className="d-flex flex-column">
            <span className="fw-semibold">{name || "—"}</span>
            <div className="text-muted small">
                {[
                    type != null ? `Type: ${type}` : null,
                    gender ? `Gender: ${gender}` : null,
                    dob ? `DOB: ${dob}` : null,
                ].filter(Boolean).join(" • ")}
            </div>
        </div>
    );
}

const EMPLOYMENT_STATUS = { "01": "Employed", "02": "Unemployed", "03": "Retired" };
const COVERAGE_TYPE = { "01": "Primary", "02": "Secondary" };

export default function CEEligibilityPanel({ userId, className, onRefetched }) {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [ce, setCe] = useState(null);
    const [error, setError] = useState(null);

    // form (editable)
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        startDate: "",
        endDate: "",
    });

    const [savingProfile, setSavingProfile] = useState(false);
    const [savingDates, setSavingDates] = useState(false);
    const [successMsg, setSuccessMsg] = useState(null);

    async function fetchCE() {
        setLoading(true);
        setError(null);
        setSuccessMsg(null);
        try {
            const res = await dispatch(getCEbyUserId({ userId }));
            const payload = res?.payload;
            setCe(payload || null);
            // init form
            if (payload) {
                setForm({
                    firstName: payload.firstName || "",
                    lastName: payload.lastName || "",
                    email: payload.email || "",
                    phone: payload.phone || "",
                    startDate: formatMDY(payload.startDate), // m-d-Y
                    endDate: formatMDY(payload.endDate),     // m-d-Y
                });
            } else {
                setForm({ firstName: "", lastName: "", email: "", phone: "", startDate: "", endDate: "" });
            }
            onRefetched && onRefetched(payload || null);
        } catch (e) {
            setError("Error fetching Client Eligibility.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { if (userId) fetchCE(); }, [userId]); // eslint-disable-line

    const isComplete = !!ce?.orderFormIsComplete;
    const employmentLabel = ce?.employmentStatus ? (EMPLOYMENT_STATUS[ce.employmentStatus] || ce.employmentStatus) : "—";
    const coverageLabel = ce?.coverageType ? (COVERAGE_TYPE[ce.coverageType] || ce.coverageType) : "—";

    const rowsTop = useMemo(() => ([
        { label: "Client Eligibility ID", value: ce?.clientEligibilityId },
        { label: "BIN", value: ce?.bin },
        { label: "PCN", value: ce?.pcn },
        { label: "Group ID", value: ce?.groupId },
        { label: "Person Code", value: ce?.personCode },
        { label: "Coverage Type", value: coverageLabel },
        { label: "Employment Status", value: employmentLabel },
    ]), [ce, coverageLabel, employmentLabel]);

    function setField(field, value) {
        setForm(prev => ({ ...prev, [field]: value }));
    }

    async function handleSaveProfile() {
        if (!ce?.memberId) return;
        setSavingProfile(true);
        setError(null);
        setSuccessMsg(null);
        try {
            // Ajustá el thunk y payload a tu API real
            const payload = {
                firstName: form.firstName,
                lastName: form.lastName,
                email: form.email,
                phone: form.phone,
            };
            await dispatch(updateClientEligibilityByMemberId({ memberId: ce.memberId, data: payload })).unwrap();
            // refresco local
            setCe(prev => ({ ...prev, ...payload }));
            setSuccessMsg("Profile updated successfully.");
        } catch (e) {
            console.error("Error updating profile:", e);
            setError("Error updating profile fields.");
        } finally {
            setSavingProfile(false);
        }
    }

    async function handleSaveDates() {
        if (!ce?.memberId) return;
        setSavingDates(true);
        setError(null);
        setSuccessMsg(null);
        try {
            // guardamos ambas fechas. Si tu endpoint acepta batch, hacelo en una sola llamada
            if (form.startDate) {
                await dispatch(updateClientEligibilityByMemberId({ memberId: ce.memberId, data: { startDate: form.startDate } })).unwrap();
            }
            if (form.endDate) {
                await dispatch(updateClientEligibilityByMemberId({ memberId: ce.memberId, data: { endDate: form.endDate } })).unwrap();
            }
            setCe(prev => ({ ...prev, startDate: form.startDate, endDate: form.endDate }));
            setSuccessMsg("Dates updated successfully.");
        } catch (e) {
            console.error("Error updating dates:", e);
            setError("Error updating dates.");
        } finally {
            setSavingDates(false);
        }
    }

    if (loading && !ce) {
        return (
            <Card className={`border ${className || ""}`}>
                <CardHeader className="d-flex justify-content-between align-items-center" style={{ background: "var(--thead-bg)" }}>
                    <h6 className="mb-0">Client Eligibility</h6>
                    <Spinner size="sm" />
                </CardHeader>
                <CardBody className="text-center">
                    <div className="d-flex align-items-center gap-2">
                        <Spinner size="sm" /> <span>Loading…</span>
                    </div>
                </CardBody>
            </Card>
        );
    }

    if (error && !ce) {
        return (
            <Card className={`border ${className || ""}`}>
                <CardHeader className="d-flex justify-content-between align-items-center" style={{ background: "var(--thead-bg)" }}>
                    <h6 className="mb-0">Client Eligibility</h6>
                </CardHeader>
                <CardBody className="text-center">
                    <Alert color="danger">{error}</Alert>
                </CardBody>
            </Card>
        );
    }

    if (!ce && !loading) {
        return (
            <Card className={`border ${className || ""}`}>
                <CardHeader className="d-flex justify-content-between align-items-center" style={{ background: "var(--thead-bg)" }}>
                    <h6 className="mb-0">Client Eligibility</h6>
                </CardHeader>
                <CardBody className="text-center">
                    <div className="text-muted">No Client Eligibility data found.</div>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card className={`border ${className || ""}`}>
            <CardHeader className="d-flex justify-content-between align-items-center" style={{ background: "var(--thead-bg)" }}>
                <h6 className="mb-0">Client Eligibility</h6>
                <div className="d-flex align-items-center gap-2">
                    {!loading && ce && (
                        <Badge color={isComplete ? "success" : "warning"} pill>
                            {isComplete ? "Eligibility Complete" : "Incomplete"}
                        </Badge>
                    )}
                    <Button size="sm" color="secondary" onClick={fetchCE} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : "Refresh"}
                    </Button>
                </div>
            </CardHeader>

            <CardBody>
                {error && <Alert color="danger">{error}</Alert>}
                {successMsg && <Alert color="success">{successMsg}</Alert>}

                {loading && !ce ? (
                    <div className="d-flex align-items-center gap-2">
                        <Spinner size="sm" /> <span>Loading…</span>
                    </div>
                ) : (
                    <>
                        {/* SECTION: Editable Profile */}
                        <Row className="g-3 mb-4">
                            <Col md={3}>
                                <Label className="text-muted mb-1">First Name</Label>
                                <Input value={form.firstName} onChange={e => setField("firstName", e.target.value)} />
                            </Col>
                            <Col md={3}>
                                <Label className="text-muted mb-1">Last Name</Label>
                                <Input value={form.lastName} onChange={e => setField("lastName", e.target.value)} />
                            </Col>
                            <Col md={3}>
                                <Label className="text-muted mb-1">Email</Label>
                                <Input type="email" value={form.email} onChange={e => setField("email", e.target.value)} />
                            </Col>
                            <Col md={3}>
                                <Label className="text-muted mb-1">Phone</Label>
                                <Input value={form.phone} onChange={e => setField("phone", e.target.value)} />
                            </Col>

                            <Col md={3}>
                                <Label className="text-muted mb-1">Start Date</Label>
                                <DatePicker
                                    value={form.startDate}
                                    onChange={(dates, dateStr) => setField("startDate", dateStr)}
                                    options={{ dateFormat: "m-d-Y" }}
                                    className="border cursor-pointer border-primary text-center rounded w-100"
                                />
                            </Col>
                            <Col md={3}>
                                <Label className="text-muted mb-1">End Date</Label>
                                <DatePicker
                                    value={form.endDate}
                                    onChange={(dates, dateStr) => setField("endDate", dateStr)}
                                    options={{ dateFormat: "m-d-Y" }}
                                    className="border cursor-pointer border-primary text-center rounded w-100"
                                />
                            </Col>

                            <Col md={6} className="d-flex align-items-end gap-2">
                                <Button color="primary" onClick={handleSaveProfile} disabled={savingProfile}>
                                    {savingProfile ? <Spinner size="sm" /> : "Save Profile"}
                                </Button>
                                <Button color="primary" onClick={handleSaveDates} disabled={savingDates}>
                                    {savingDates ? <Spinner size="sm" /> : "Save Dates"}
                                </Button>
                            </Col>
                        </Row>

                        {/* SECTION: Core IDs & Plan */}
                        <Row className="g-3 mb-3">
                            {rowsTop.map((r) => (
                                <Col key={r.label} md={4}>{smallMuted(r.label, r.value)}</Col>
                            ))}
                        </Row>

                        {/* SECTION: Consents & Dates (read-only) */}
                        <Row className="g-3 mb-4">
                            <Col md={4}>{smallMuted("HIPAA Consent Date", fmtDate(ce?.hipaaConsent || ce?.hippaConsent))}</Col>
                            <Col md={4}>{smallMuted("Privacy Consent Date", fmtDate(ce?.privacyConsent))}</Col>
                            <Col md={4}>{smallMuted("Order Form Date", fmtDate(ce?.orderFormDate))}</Col>
                        </Row>

                        {/* SECTION: Clinical Info */}
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <div className="text-muted mb-2">Allergies</div>
                                {Array.isArray(ce?.allergies) && ce.allergies.length > 0 ? (
                                    <ListGroup>
                                        {ce.allergies.map((a) => (
                                            <ListGroupItem key={a?.patientAllergyId || a?.allergyId}>
                                                {renderAllergy(a)}
                                            </ListGroupItem>
                                        ))}
                                    </ListGroup>
                                ) : (
                                    <div className="text-muted">No allergies on file</div>
                                )}
                            </Col>

                            <Col md={6}>
                                <div className="text-muted mb-2">Medical Conditions</div>
                                {Array.isArray(ce?.medicalConditions) && ce.medicalConditions.length > 0 ? (
                                    <ListGroup>
                                        {ce.medicalConditions.map((c, idx) => (
                                            <ListGroupItem key={c?.id || c?.medicalConditionId || idx}>
                                                {renderCondition(c)}
                                            </ListGroupItem>
                                        ))}
                                    </ListGroup>
                                ) : (
                                    <div className="text-muted">No medical conditions on file</div>
                                )}
                            </Col>
                        </Row>

                        {/* SECTION: Dependents */}
                        <div className="mb-2 text-muted">Dependents</div>
                        {Array.isArray(ce?.dependents) && ce.dependents.length > 0 ? (
                            <Row className="g-3">
                                {ce.dependents.map((d, idx) => (
                                    <Col md={6} key={idx}>
                                        <Card className="border">
                                            <CardBody>{renderDependent(d)}</CardBody>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            <div className="text-muted">No dependents on file</div>
                        )}
                    </>
                )}
            </CardBody>
        </Card>
    );
}